/**
 * A site's allowed host patterns (task 303, ADR 010).
 *
 * A site lists the hosts its reports may come from. Each entry is an exact
 * host (`shop.example.com`) or a leading wildcard (`*.example.com`). This
 * module is the one grammar for them: the dashboard API validates with
 * `parseHostPattern`, the web form shows the same errors before it submits,
 * and ingestion matches with `matchesHostPatterns`.
 *
 * It does no I/O and imports nothing Node-specific, so it is safe in the
 * browser. It is exported from `@headerhawk/core/hosts` rather than the
 * package barrel because it carries the Public Suffix List (`tldts`), which
 * only the code that needs it should pay for.
 */
import { parse as parseDomain } from "tldts";
/**
 * The most host patterns one site may have.
 *
 * Chosen, not measured. It bounds the per-request match cost on the ingestion
 * hot path (a linear scan over the list for every report) and what a single
 * `PATCH /sites/{siteId}` can write. It is not a plan limit: plans meter
 * sites and report volume, and wildcards are on every plan.
 */
export const MAX_HOSTS_PER_SITE = 50;
/** Longest host name DNS allows, in its ASCII form. */
const MAX_HOST_LENGTH = 253;
/** Longest DNS label. */
const MAX_LABEL_LENGTH = 63;
function reject(reason, message) {
    return { ok: false, error: { reason, message } };
}
/**
 * Letters and marks (for an internationalised name, which is converted to
 * punycode below), digits, hyphens, underscores and dots. Anything else - a
 * scheme, a path, a port, userinfo, whitespace - is refused before `URL` gets
 * the chance to reinterpret it.
 */
const HOST_CHARACTERS = /^[\p{L}\p{M}\p{N}_.-]+$/u;
/**
 * Normalise one host: lowercase, punycode, and DNS-shaped.
 *
 * `URL` does the IDNA conversion, in Node and in the browser alike, which is
 * the same conversion a browser applied to the `document-uri` a report
 * carries - so a stored pattern and a reported host meet in the same form.
 */
function normaliseHost(input) {
    if (!HOST_CHARACTERS.test(input)) {
        return {
            ok: false,
            error: {
                reason: "not_a_host",
                message: "Enter a host name only, such as shop.example.com",
            },
        };
    }
    let host;
    try {
        host = new URL(`http://${input}`).hostname;
    }
    catch {
        return {
            ok: false,
            error: { reason: "not_a_host", message: "This is not a valid host name" },
        };
    }
    if (host.length > MAX_HOST_LENGTH) {
        return {
            ok: false,
            error: {
                reason: "too_long",
                message: `A host name can be at most ${MAX_HOST_LENGTH} characters`,
            },
        };
    }
    const labels = host.split(".");
    for (const label of labels) {
        if (label.length === 0 ||
            label.length > MAX_LABEL_LENGTH ||
            label.startsWith("-") ||
            label.endsWith("-")) {
            return {
                ok: false,
                error: {
                    reason: "not_a_host",
                    message: "This is not a valid host name",
                },
            };
        }
    }
    return { ok: true, host };
}
/**
 * Parse one host pattern.
 *
 * - `Shop.Example.com` is stored as `shop.example.com`.
 * - `*.example.com` is a wildcard on `example.com`: it matches subdomains at
 *   any depth, never `example.com` itself.
 * - `*` is allowed only as the whole first label.
 * - A wildcard over a public suffix (`*.co.uk`, and `*.github.io` from the
 *   list's private section) is refused: it would accept every customer of
 *   that suffix.
 * - `localhost` and IP addresses are allowed as exact hosts only.
 * - A scheme, path or port is refused. Browsers report the document's host
 *   without its port, so a pattern with one could never match.
 */
export function parseHostPattern(input) {
    const trimmed = input.trim().toLowerCase();
    if (trimmed.length === 0) {
        return reject("empty", "Enter a host name");
    }
    if (/^[a-z][a-z0-9+.-]*:\/\//.test(trimmed) || /[/?#]/.test(trimmed)) {
        return reject("not_a_host", "Enter the host only, without https:// or a path");
    }
    if (/:\d*$/.test(trimmed) && !trimmed.startsWith("[")) {
        return reject("port", "Leave out the port: browsers report the host without it");
    }
    const isWildcard = trimmed.startsWith("*.");
    const rest = isWildcard ? trimmed.slice(2) : trimmed;
    if (rest.includes("*")) {
        return reject("wildcard_position", "A wildcard can only be the whole first label, as in *.example.com");
    }
    const normalised = normaliseHost(rest);
    if (!normalised.ok)
        return normalised;
    const { host } = normalised;
    if (!isWildcard) {
        return { ok: true, value: { pattern: host, kind: "exact", host } };
    }
    const parsed = parseDomain(host, { allowPrivateDomains: true });
    if (parsed.isIp || /^\d+$/.test(host.slice(host.lastIndexOf(".") + 1))) {
        return reject("wildcard_ip", "An IP address cannot have a wildcard. Add the address itself");
    }
    if (parsed.domain === null) {
        return reject("wildcard_public_suffix", `*.${host} would accept every site under ${host}. Use your own domain, as in *.example.${host}`);
    }
    return {
        ok: true,
        value: { pattern: `*.${host}`, kind: "wildcard", host },
    };
}
/**
 * Parse a whole submitted list, reporting every refused entry by index so a
 * form can place each error next to its row.
 *
 * Two entries that normalise to the same pattern are refused as a duplicate
 * (the second one), rather than silently collapsed, so what a user sees saved
 * is what they typed. The list's length is the caller's to check: an empty
 * list and one over `MAX_HOSTS_PER_SITE` are errors about the list, not about
 * any entry in it.
 */
export function parseHostList(inputs) {
    const patterns = [];
    const seen = new Set();
    const rejections = [];
    inputs.forEach((input, index) => {
        if (typeof input !== "string") {
            rejections.push({
                index,
                input: String(input),
                reason: "not_a_host",
                message: "Each host must be a string",
            });
            return;
        }
        const result = parseHostPattern(input);
        if (!result.ok) {
            rejections.push({ index, input, ...result.error });
            return;
        }
        if (seen.has(result.value.pattern)) {
            rejections.push({
                index,
                input,
                reason: "duplicate",
                message: `${result.value.pattern} is already in the list`,
            });
            return;
        }
        seen.add(result.value.pattern);
        patterns.push(result.value.pattern);
    });
    return rejections.length > 0
        ? { ok: false, rejections }
        : { ok: true, patterns };
}
/** Hosts ingestion derives when a document URI has no usable host. */
const UNMATCHABLE_HOSTS = new Set(["", "invalid", "unknown"]);
/**
 * Does a report's document host match any of a site's patterns?
 *
 * Exact patterns compare equal after lowercasing. A wildcard `*.d` requires
 * the host to end in `.d` - never merely `d`, which would let
 * `notexample.com` through `*.example.com` - so the bare domain does not
 * match its own wildcard, as with a TLS certificate.
 *
 * Patterns are expected in their stored, normalised form. This is on the
 * ingestion hot path, so it parses nothing.
 */
export function matchesHostPatterns(documentHost, patterns) {
    const host = documentHost.trim().toLowerCase();
    if (UNMATCHABLE_HOSTS.has(host))
        return false;
    for (const pattern of patterns) {
        if (pattern.startsWith("*.")) {
            if (host.endsWith(pattern.slice(1)))
                return true;
        }
        else if (host === pattern) {
            return true;
        }
    }
    return false;
}
/**
 * The registrable domain (eTLD+1) of a host, from the Public Suffix List with
 * its private section: `blog.example.co.uk` gives `example.co.uk`, and
 * `foo.github.io` gives `foo.github.io`. Undefined for an IP address, a
 * public suffix on its own, and a single-label name such as `localhost`.
 */
export function registrableDomain(host) {
    const parsed = parseDomain(host.trim().toLowerCase(), {
        allowPrivateDomains: true,
    });
    if (parsed.isIp)
        return undefined;
    return parsed.domain ?? undefined;
}
/**
 * The wildcard one level below a host, `*.<host>`, or undefined where the
 * host has no registrable domain (an IP address, `localhost`, a public suffix
 * on its own).
 *
 * This is what "Also accept all subdomains" adds in the dashboard: the
 * subdomains of the host as typed, never of its registrable domain, so ticking
 * it beside `dev.example.com` cannot also accept `www.example.com` (task 418).
 */
export function subdomainWildcardFor(host) {
    const h = host.trim().toLowerCase();
    return registrableDomain(h) ? `*.${h}` : undefined;
}
/**
 * The narrowest wildcard that covers a host and its siblings, `*.<parent>`,
 * or undefined when the host is not a subdomain of its registrable domain.
 *
 * `docs-3.sandbox.example.com` gives `*.sandbox.example.com`, and
 * `shop.example.com` gives `*.example.com`; `example.com` and `foo.github.io`
 * give nothing, since a wildcard over their parent would be a public suffix.
 * The drop notice offers it beside a host a browser sent (task 418).
 */
export function parentWildcardFor(host) {
    const h = host.trim().toLowerCase();
    const domain = registrableDomain(h);
    if (!domain || domain === h)
        return undefined;
    // Offer only what the API would store: a browser can send a host the
    // grammar refuses, such as a fully qualified `shop.example.com.`.
    const parsed = parseHostPattern(`*.${h.slice(h.indexOf(".") + 1)}`);
    return parsed.ok ? parsed.value.pattern : undefined;
}
/**
 * Is a rejected host plausibly one of the site owner's own, and so worth
 * naming on the drop notice?
 *
 * Related to any pattern when it is a subdomain or a parent of the pattern's
 * host (with a `.` boundary both ways), or when the two share a registrable
 * domain. The last is what names `blog.example.com` for a site that allows
 * only `www.example.com`.
 *
 * The Public Suffix List is what makes the sibling rule safe. The ingest code
 * is public, so anyone can post a report claiming any host; a naive "same last
 * two labels" rule would let them name every host under `.co.uk` in a UK
 * tenant's settings page. With the list, only hosts under the tenant's own
 * registrable domain are named, and the private section keeps one
 * `github.io` site from naming another.
 */
export function isRelatedToPatterns(host, patterns) {
    const h = host.trim().toLowerCase();
    if (UNMATCHABLE_HOSTS.has(h))
        return false;
    const hostDomain = registrableDomain(h);
    for (const pattern of patterns) {
        const d = pattern.startsWith("*.") ? pattern.slice(2) : pattern;
        if (!d)
            continue;
        // Equal is only reachable for a wildcard's own domain - an exact pattern
        // equal to the host would have matched - and that bare domain is the most
        // common thing a wildcard-only site forgets to allow.
        if (h === d || h.endsWith(`.${d}`) || d.endsWith(`.${h}`))
            return true;
        if (hostDomain !== undefined && registrableDomain(d) === hostDomain) {
            return true;
        }
    }
    return false;
}
//# sourceMappingURL=index.js.map